import { forwardRef } from 'react';
import Select, { ActionMeta, GroupBase, Props as ReactSelectProps, SelectInstance, SingleValue } from 'react-select';
import { combineClassNames } from '../../../Utils/ClassnameUtils';

export type GWPSearchSelectOption<T extends string | number> = {
	value: T;
	label: string;
	isDisabled?: boolean;
};

export interface GWPSearchSelectProps<T extends string | number>
	extends Omit<
		ReactSelectProps<GWPSearchSelectOption<T>, false, GroupBase<GWPSearchSelectOption<T>>>,
		'isMulti' | 'className' | 'classNamePrefix' | 'unstyled' | 'value' | 'defaultValue' | 'onChange'
	> {
	className?: string;
	id?: string;
	options: ReadonlyArray<GWPSearchSelectOption<T>>;
	value?: GWPSearchSelectOption<T> | null;
	defaultValue?: GWPSearchSelectOption<T> | null;
	size?: 'sm' | 'md' | 'lg';
	invalid?: boolean;
	onChange?: (
		newValue: SingleValue<GWPSearchSelectOption<T>>,
		actionMeta: ActionMeta<GWPSearchSelectOption<T>>,
	) => void;
	onValueChange?: (
		option: GWPSearchSelectOption<T> | null,
		actionMeta: ActionMeta<GWPSearchSelectOption<T>>,
	) => void;
}

type GWPSearchSelectComponent = <T extends string | number>(
	props: GWPSearchSelectProps<T> &
		React.RefAttributes<SelectInstance<GWPSearchSelectOption<T>, false, GroupBase<GWPSearchSelectOption<T>>>>,
) => React.ReactElement | null;

const GWPSearchSelectInner = forwardRef(function GWPSearchSelectInner<T extends string | number>(
	props: GWPSearchSelectProps<T>,
	ref: React.ForwardedRef<SelectInstance<GWPSearchSelectOption<T>, false, GroupBase<GWPSearchSelectOption<T>>>>,
) {
	const {
		className,
		id,
		inputId,
		instanceId,
		size = 'md',
		invalid = false,
		isDisabled = false,
		menuPlacement = 'auto',
		onChange,
		onValueChange,
		...rest
	} = props;

	const rootClassName = combineClassNames(
		'gwp-search-select',
		size,
		invalid ? 'invalid' : undefined,
		isDisabled ? 'disabled' : undefined,
		className,
	);

	const handleChange = (
		newValue: SingleValue<GWPSearchSelectOption<T>>,
		actionMeta: ActionMeta<GWPSearchSelectOption<T>>,
	) => {
		onChange?.(newValue, actionMeta);
		onValueChange?.(newValue ?? null, actionMeta);
	};

	return (
		<Select<GWPSearchSelectOption<T>, false, GroupBase<GWPSearchSelectOption<T>>>
			{...rest}
			ref={ref}
			inputId={inputId ?? id}
			instanceId={instanceId ?? inputId ?? id}
			className={rootClassName}
			classNamePrefix="gwp-search-select"
			isDisabled={isDisabled}
			isMulti={false}
			menuPlacement={menuPlacement}
			onChange={handleChange}
			unstyled
		/>
	);
});

GWPSearchSelectInner.displayName = 'GWPSearchSelect';

const GWPSearchSelect = GWPSearchSelectInner as GWPSearchSelectComponent;

export default GWPSearchSelect;
